import { useEffect, useRef } from "react";
import { studyTimeService } from "@/services/studyTimeService";
import { useAuth } from "@/contexts/AuthContext";

interface UseStudyTimeTrackerProps {
  activityType: string;
  competency: string;
  section?: string | null;
  metadata?: any;
  enabled?: boolean;
}

export function useStudyTimeTracker({
  activityType,
  competency,
  section = null,
  metadata = {},
  enabled = true
}: UseStudyTimeTrackerProps) {
  const { user, loading } = useAuth();
  const sessionIdRef = useRef<string | null>(null);
  const elapsedSecondsRef = useRef<number>(0);
  const lastSyncedSecondsRef = useRef<number>(0);
  const lastInteractionTimeRef = useRef<number>(Date.now());

  // Refs for props to access fresh values in intervals without restarting
  const activityTypeRef = useRef(activityType);
  const competencyRef = useRef(competency);
  const sectionRef = useRef(section);
  const metadataRef = useRef(metadata);

  useEffect(() => {
    activityTypeRef.current = activityType;
    competencyRef.current = competency;
    sectionRef.current = section;
    metadataRef.current = metadata;
  }, [activityType, competency, section, metadata]);

  useEffect(() => {
    if (!enabled || loading) {
      return;
    }

    let isMounted = true;
    const sessionActivityType = activityTypeRef.current;
    const sessionCompetency = competencyRef.current;
    const sessionSection = sectionRef.current;
    const sessionMetadata = metadataRef.current;

    // 1. Initialize session
    const initSession = async () => {
      try {
        const id = await studyTimeService.startStudySession({
          activityType: sessionActivityType,
          competency: sessionCompetency,
          section: sessionSection,
          metadata: sessionMetadata
        });
        if (isMounted) {
          sessionIdRef.current = id;
          elapsedSecondsRef.current = 0;
          lastSyncedSecondsRef.current = 0;
          lastInteractionTimeRef.current = Date.now();
        }
      } catch (err) {
        console.error("[useStudyTimeTracker] Failed to start study session:", err);
      }
    };

    initSession();

    // 2. Track interactions
    const handleInteraction = () => {
      lastInteractionTimeRef.current = Date.now();
    };

    const events = ["mousemove", "click", "keydown", "scroll", "touchstart"];
    events.forEach(event => {
      window.addEventListener(event, handleInteraction, { passive: true });
    });

    // 3. Set up active count interval (1s)
    const interval = setInterval(async () => {
      if (!sessionIdRef.current) return;

      // Rule A: Tab must be visible
      const isVisible = document.visibilityState === "visible";

      // Rule B: Active interaction in the last 60 seconds
      const isUserActive = Date.now() - lastInteractionTimeRef.current <= 60000;

      if (isVisible && isUserActive) {
        elapsedSecondsRef.current += 1;

        // Save progress every 30 seconds to prevent data loss
        if (elapsedSecondsRef.current - lastSyncedSecondsRef.current >= 30) {
          const currentElapsed = elapsedSecondsRef.current;
          lastSyncedSecondsRef.current = currentElapsed;
          try {
            await studyTimeService.updateStudySessionDuration(
              sessionIdRef.current,
              currentElapsed
            );
          } catch (err) {
            console.error("[useStudyTimeTracker] Error updating duration:", err);
          }
        }
      }
    }, 1000);

    // 4. Cleanup when disabling or unmounting
    return () => {
      isMounted = false;
      clearInterval(interval);
      
      events.forEach(event => {
        window.removeEventListener(event, handleInteraction);
      });

      const finalSessionId = sessionIdRef.current;
      const finalDuration = elapsedSecondsRef.current;

      if (finalSessionId) {
        // Run sync in background on unmount
        (async () => {
          try {
            await studyTimeService.updateStudySessionDuration(finalSessionId, finalDuration);
            await studyTimeService.endStudySession(finalSessionId);
          } catch (err) {
            console.error("[useStudyTimeTracker] Error ending session on cleanup:", err);
          }
        })();
        
        sessionIdRef.current = null;
      }
    };
  }, [enabled, activityType, competency, section, loading, user?.id]); // Restart session if key properties or auth state changes
}


